// Per-tile stream state. Each tile fires its own streamQuery on mount and
// updates its own CurrentTurn-shaped state, independently of the global
// useChatState. Reuses applyEvent so every shell processes SSE events
// identically.
import { useEffect, useRef, useState } from 'react';
import { streamQuery } from '../../api';
import { applyEvent } from '../../useChatState';
import type { CurrentTurn } from '../types';
import type { AssistantTurn, StreamEvent } from '../../types';

const EMPTY_PRESENTATION: AssistantTurn['presentation'] = { type: 'prose' };

export interface TileState extends CurrentTurn {
  /** key of the standing query that produced this tile, or null for added tiles */
  sourceKey: string | null;
  /** Refinement chip displayed in tile header if this tile was modified */
  refinedTag?: string;
}

function newTurn(query: string, sourceKey: string | null, refinedTag?: string): TileState {
  return {
    query,
    assistant: {
      interactionId: '',
      toolsUsed: [],
      prose: '',
      presentation: EMPTY_PRESENTATION,
      citations: [],
    },
    toolsInFlight: [],
    streaming: true,
    sourceKey,
    refinedTag,
  };
}

/** Run a single query through the SSE pipeline and return its evolving TileState. */
export function useTileStream(query: string, sourceKey: string | null = null, refinedTag?: string): TileState {
  const [state, setState] = useState<TileState>(() => newTurn(query, sourceKey, refinedTag));
  const startedFor = useRef<string | null>(null);

  useEffect(() => {
    // Re-fire when the query changes (e.g. after a refinement).
    if (startedFor.current === query) return;
    startedFor.current = query;
    setState(newTurn(query, sourceKey, refinedTag));

    const ctl = new AbortController();
    streamQuery(
      { query, prior_turns: [] },
      {
        signal: ctl.signal,
        onEvent: (ev: StreamEvent) => {
          setState((prev) => {
            const next = applyEvent(prev, ev);
            return { ...next, sourceKey: prev.sourceKey, refinedTag: prev.refinedTag };
          });
        },
        onError: (err) => {
          if (ctl.signal.aborted) return;
          setState((prev) => ({
            ...prev,
            streaming: false,
            assistant: { ...prev.assistant, error: err.message },
          }));
        },
      },
    );
    return () => ctl.abort();
  }, [query, sourceKey, refinedTag]);

  return state;
}
